import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ActivitySession, ActivitySurface, getActivitySessions } from '../../../services/adminApi';
import { formatTimeAgo, formatTimestamp } from '../../../utils/dateFormatters';
import { Button, EmptyState, Spinner, StatusPill, Tabs, tableStyles } from '../../ui';
import {
  dashboardLabel,
  formatSessionLength,
  isActiveNow,
  orgLabel,
  orgVariant,
  usedSummary,
  whereLabel,
} from './loginActivityFormat';
import { loginActivityStyles as styles } from './loginActivityStyles';

const PAGE_SIZE = 50;
const COLUMNS = 6;

type SurfaceFilter = 'all' | ActivitySurface;

const SURFACE_OPTIONS: Array<{ value: SurfaceFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'web', label: 'Web' },
  { value: 'connector', label: 'Claude' },
];

const FAILED_HINT = 'Calls that returned an error to Claude, including permission refusals';

/** Sittings in the web app and through the Claude connector, newest first. */
const SessionsView: React.FC = () => {
  const [sessions, setSessions] = useState<ActivitySession[]>([]);
  const [cursor, setCursor] = useState<number | null>(null);
  const [surface, setSurface] = useState<SurfaceFilter>('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const requestSeqRef = useRef(0);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(handle);
  }, [search]);

  const fetchPage = useCallback(
    (before?: number) =>
      getActivitySessions({
        limit: PAGE_SIZE,
        before,
        search: debouncedSearch || undefined,
        surface: surface === 'all' ? undefined : surface,
      }),
    [debouncedSearch, surface],
  );

  useEffect(() => {
    const seq = ++requestSeqRef.current;
    setLoading(true);
    setError(null);
    setExpandedId(null);
    fetchPage()
      .then((result) => {
        if (seq !== requestSeqRef.current) return;
        setSessions(result.sessions);
        setCursor(result.nextCursor);
      })
      .catch((err: any) => {
        if (seq !== requestSeqRef.current) return;
        setError(err?.message || 'Failed to load sessions.');
        setSessions([]);
        setCursor(null);
      })
      .finally(() => {
        if (seq === requestSeqRef.current) setLoading(false);
      });
  }, [fetchPage]);

  const handleLoadMore = useCallback(async () => {
    if (cursor == null || loadingMore) return;
    const seq = ++requestSeqRef.current;
    setLoadingMore(true);
    try {
      const result = await fetchPage(cursor);
      if (seq !== requestSeqRef.current) return;
      setSessions((prev) => [...prev, ...result.sessions]);
      setCursor(result.nextCursor);
    } catch (err: any) {
      if (seq !== requestSeqRef.current) return;
      setError(err?.message || 'Failed to load more.');
    } finally {
      if (seq === requestSeqRef.current) setLoadingMore(false);
    }
  }, [cursor, fetchPage, loadingMore]);

  const filtered = Boolean(debouncedSearch) || surface !== 'all';

  return (
    <>
      <div style={styles.toolbar}>
        <Tabs<SurfaceFilter>
          value={surface}
          onChange={setSurface}
          options={SURFACE_OPTIONS}
          size="sm"
          ariaLabel="Show sessions from"
        />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or email"
          style={styles.searchInput}
          aria-label="Search sessions"
        />
      </div>

      {error && <div style={styles.errorBanner}>{error}</div>}

      {loading ? (
        <div style={styles.loadingRow}>
          <Spinner label="Loading sessions…" />
        </div>
      ) : sessions.length === 0 ? (
        <EmptyState
          brand={false}
          maxWidth={480}
          title={filtered ? 'No matching sessions' : 'No sessions yet'}
          subtitle={
            filtered
              ? 'No sessions match that search or filter. Try a different name, email or filter.'
              : 'Sessions appear here as people use the dashboards or Genius through Claude. Earlier sign-ins are under Sign-ins.'
          }
        />
      ) : (
        <>
          <div style={{ ...tableStyles.wrapScroll, marginBottom: 16 }}>
            <table style={tableStyles.table}>
              <thead>
                <tr>
                  <th style={{ ...tableStyles.th, width: 28, paddingRight: 0 }}>
                    <span style={rowStyles.visuallyHidden}>Details</span>
                  </th>
                  <th style={tableStyles.th}>User</th>
                  <th style={tableStyles.th}>Where</th>
                  <th style={tableStyles.th}>Started</th>
                  <th style={tableStyles.th}>Length</th>
                  {/* On a phone the table scrolls sideways; without a floor this column shrinks to one word per line. */}
                  <th style={{ ...tableStyles.th, minWidth: 240 }}>Used</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => (
                  <SessionRow
                    key={s.id}
                    session={s}
                    expanded={expandedId === s.id}
                    onToggle={() => setExpandedId((id) => (id === s.id ? null : s.id))}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {cursor != null && (
            <div style={styles.loadMoreRow}>
              <Button variant="ghost" size="sm" onClick={handleLoadMore} disabled={loadingMore}>
                {loadingMore ? 'Loading…' : 'Load more'}
              </Button>
            </div>
          )}
        </>
      )}
    </>
  );
};

const SessionRow: React.FC<{ session: ActivitySession; expanded: boolean; onToggle: () => void }> = ({
  session: s,
  expanded,
  onToggle,
}) => {
  const expandable = s.dashboards.length > 0 || s.tools.length > 0;
  return (
    <>
      <tr
        onClick={expandable ? onToggle : undefined}
        onMouseEnter={(ev) => {
          if (!expanded) ev.currentTarget.style.background = tableStyles.rowHoverBackground;
        }}
        onMouseLeave={(ev) => {
          if (!expanded) ev.currentTarget.style.background = '';
        }}
        style={{
          cursor: expandable ? 'pointer' : 'default',
          background: expanded ? tableStyles.trExpanded.background : undefined,
        }}
      >
        <td style={{ ...tableStyles.td, width: 28, paddingRight: 0 }}>
          {expandable && (
            <button
              type="button"
              onClick={(ev) => {
                ev.stopPropagation();
                onToggle();
              }}
              aria-expanded={expanded}
              aria-label={`${expanded ? 'Hide' : 'Show'} details for ${s.displayName || s.email}`}
              style={rowStyles.toggle}
            >
              {expanded ? '▼' : '▶'}
            </button>
          )}
        </td>
        <td style={tableStyles.td}>
          <div style={rowStyles.name}>
            {s.displayName || s.email}
            {s.organization !== 'culturegenesis' && (
              <StatusPill variant={orgVariant(s.organization)} noDot style={{ marginLeft: 8 }}>
                {orgLabel(s.organization)}
              </StatusPill>
            )}
          </div>
          {s.displayName && <div style={rowStyles.email}>{s.email}</div>}
        </td>
        <td style={tableStyles.td}>
          <div style={rowStyles.pills}>
            <StatusPill variant={s.surface === 'web' ? 'approved' : 'featured'} noDot>
              {whereLabel(s)}
            </StatusPill>
            {s.signInAt && (
              <span title={`Signed in ${formatTimestamp(s.signInAt)}`}>
                <StatusPill variant="ok" noDot>
                  Signed in
                </StatusPill>
              </span>
            )}
          </div>
        </td>
        <td style={{ ...tableStyles.td, whiteSpace: 'nowrap' }} title={formatTimestamp(s.startedAt)}>
          {formatTimeAgo(s.startedAt)}
        </td>
        <td style={{ ...tableStyles.td, whiteSpace: 'nowrap' }} title={`Last activity ${formatTimestamp(s.lastActiveAt)}`}>
          {formatSessionLength(s.startedAtMillis, s.lastActiveAtMillis)}
          {isActiveNow(s) && (
            <StatusPill variant="live" style={{ marginLeft: 8 }}>
              Active
            </StatusPill>
          )}
        </td>
        <td style={tableStyles.td}>
          <UsedCell session={s} />
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={COLUMNS} style={rowStyles.detailTd}>
            {s.surface === 'web' ? <WebDetail session={s} /> : <ConnectorDetail session={s} />}
          </td>
        </tr>
      )}
    </>
  );
};

const UsedCell: React.FC<{ session: ActivitySession }> = ({ session }) => {
  const used = usedSummary(session);
  if (!used.lead && used.items.length === 0) {
    return <span style={rowStyles.muted}>No dashboards opened</span>;
  }
  return (
    <span>
      {used.lead && <span style={rowStyles.lead}>{used.lead}</span>}
      {used.lead && used.items.length > 0 && ' · '}
      {used.items.join(', ')}
      {used.more > 0 && <span style={rowStyles.muted}> +{used.more} more</span>}
      {used.failed > 0 && (
        <span style={rowStyles.failed} title={FAILED_HINT}>
          {' '}
          · {used.failed} failed
        </span>
      )}
    </span>
  );
};

const WebDetail: React.FC<{ session: ActivitySession }> = ({ session }) => (
  <div style={rowStyles.detail}>
    <div style={rowStyles.detailHeading}>Dashboards opened</div>
    <ul style={rowStyles.detailList}>
      {session.dashboards.map((d) => (
        <li key={d.id} style={rowStyles.detailItem}>
          <span>{dashboardLabel(d.id)}</span>
          <span style={rowStyles.detailCount}>{d.opens === 1 ? 'opened once' : `opened ${d.opens} times`}</span>
        </li>
      ))}
    </ul>
    <div style={rowStyles.detailMeta}>
      {session.signInAt && `Signed in ${formatTimestamp(session.signInAt)} · `}
      Started {formatTimestamp(session.startedAt)} · last activity {formatTimestamp(session.lastActiveAt)}
    </div>
  </div>
);

const ConnectorDetail: React.FC<{ session: ActivitySession }> = ({ session }) => (
  <div style={rowStyles.detail}>
    <div style={rowStyles.detailHeading}>Genius tools called through {whereLabel(session)}</div>
    <ul style={rowStyles.detailList}>
      {session.tools.map((t) => (
        <li key={t.name} style={rowStyles.detailItem}>
          <span>
            {t.title} <code style={rowStyles.code}>{t.name}</code>
          </span>
          <span style={rowStyles.detailCount}>
            {t.calls === 1 ? '1 call' : `${t.calls} calls`}
            {t.errors > 0 && (
              <span style={rowStyles.failed} title={FAILED_HINT}>
                {' '}
                · {t.errors} failed
              </span>
            )}
          </span>
        </li>
      ))}
    </ul>
    <div style={rowStyles.detailMeta}>
      Started {formatTimestamp(session.startedAt)} · last call {formatTimestamp(session.lastActiveAt)}
      {session.connector?.gid && ` · connection ${session.connector.gid.slice(0, 8)}`}
    </div>
  </div>
);

const rowStyles: Record<string, React.CSSProperties> = {
  toggle: {
    background: 'transparent',
    border: 'none',
    padding: 4,
    fontSize: 10,
    lineHeight: 1,
    color: 'var(--fg-3)',
    cursor: 'pointer',
  },
  name: {
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    rowGap: 4,
  },
  email: {
    fontSize: 12,
    color: 'var(--fg-3)',
    marginTop: 2,
  },
  pills: {
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap',
  },
  lead: {
    fontWeight: 600,
  },
  muted: {
    color: 'var(--fg-3)',
  },
  failed: {
    color: 'var(--danger)',
  },
  detailTd: {
    padding: '0 12px 16px 40px',
    background: 'var(--bg-surface-3)',
    borderBottom: '1px solid var(--border-subtle)',
  },
  detail: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    paddingTop: 4,
    maxWidth: 640,
  },
  detailHeading: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 'var(--track-eyebrow)',
    textTransform: 'uppercase',
    color: 'var(--fg-4)',
  },
  detailList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  detailItem: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 16,
    fontSize: 13,
    color: 'var(--fg-1)',
  },
  detailCount: {
    color: 'var(--fg-3)',
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
  },
  code: {
    fontFamily: 'var(--font-mono)',
    fontSize: 11.5,
    color: 'var(--fg-3)',
    marginLeft: 4,
  },
  detailMeta: {
    fontSize: 12,
    color: 'var(--fg-3)',
  },
  visuallyHidden: {
    position: 'absolute',
    width: 1,
    height: 1,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
  },
};

export default SessionsView;
